import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';

import app from 'app/config/app';
import isEmpty from 'helpers/isEmpty';
import debounce from 'helpers/debounce';
import http from 'app/services/httpService';
import apiEndpoints, { buildApiRoute } from 'routes/apiEndpoints';

export default function SelectFundingSource(props) {
  const {
    id,
    name,
    label,
    error,
    helperText,
    required,
    size,
    disabled,
    fullWidth,
    variant,
    value,
    onSelected,
    valueKey = 'payment_value',
    ...rest
  } = props;

  const [selectedValue, setSelectedValue] = useState({ label: '---SELECT---', value: '' });
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [params, setParams] = useState({
    page_size: 400,
    page_number: 1,
    sort_by: 'payment_name',
    order_by: 'asc',
  });

  const handleSearchChange = (e) => {
    debounce(() => {
      setParams({
        ...params,
        search: e.target.value,
      });
    }, 500);
  };

  /**
   * fetch fundingSources
   */
  const fetchFundingSources = async () => {
    setIsLoading(true);
    try {
      let endpoint = `${app.apiBaseUrl + apiEndpoints.getFundingSources}?${new URLSearchParams(
        params,
      )}`;
      let response = await http.get(endpoint);
      let { data, pagination } = response.data;

      if (data) {
        data = data.map((p) => ({ label: p.payment_name, value: p[valueKey] || p.payment_value }));
        let index = options.findIndex((o) => o.id === selectedValue?.payment_value);
        if (index === -1) data.unshift(selectedValue);
        setOptions(data);
        setIsLoading(true);
      }
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
  };

  /**
   * handle on autocomplete change
   */
  const handleChange = (e, value) => {
    e.preventDefault();
    setSelectedValue(value);
    if (typeof onSelected === 'function') onSelected(value?.value || undefined);
  };

  /**
   * fetch fundingSource
   */
  const fetchPartner = async (fundingSourceId) => {
    try {
      let endpoint = buildApiRoute(apiEndpoints.getFundingSource, fundingSourceId);
      let response = await http.get(endpoint);
      let fundingSource = response?.data?.data;

      if (fundingSource) {
        let index = options.findIndex((o) => o?.value === fundingSource.id);
        if (index === -1)
          setOptions([
            ...options,
            { label: fundingSource.payment_name, value: fundingSource[valueKey] },
          ]);
        setSelectedValue({ label: fundingSource.payment_name, value: fundingSource.id });
      }
    } catch (err) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (params?.search?.length > 1) fetchFundingSources();
  }, [JSON.stringify(params)]);

  useEffect(() => {
    if (isEmpty(value)) fetchFundingSources();
  }, []);

  useEffect(() => {
    if (!value || !params.search) fetchFundingSources();
  }, [value, params]);

  // useEffect(() => {
  //   if (!open) setOptions([]);
  // }, [open]);

  useEffect(() => {
    if (value) fetchPartner(value);
  }, []);

  return (
    <Autocomplete
      id={id}
      value={selectedValue}
      // {...(value ? { value: selectedValue } : {})}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onChange={handleChange}
      options={options}
      loading={isLoading}
      autoHighlight
      getOptionLabel={(option) => option?.label ?? ''}
      isOptionEqualToValue={(option, value) => option?.value === value?.value}
      renderOption={(props, option) => (
        <Box
          component="li"
          sx={{ '& > img': { px: 2, py: 4, flexShrink: 0, border: '1px solid red' } }}
          {...props}
          key={option?.value ?? 't' + Math.random()}
        >
          {option?.label}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          onChange={handleSearchChange}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          variant={variant}
          fullWidth={fullWidth}
          error={error}
          helperText={helperText}
          required={required}
        />
      )}
      size={size}
      {...rest}
    />
  );
}

SelectFundingSource.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['medium', 'small', 'large']),
  fullWidth: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(['outlined', 'standard', 'filled']),
  color: PropTypes.oneOf(['primary', 'secondary', 'error', 'info', 'success', 'warning']),
  onSelected: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.string,
  valueKey: PropTypes.string,
};

SelectFundingSource.defaultProps = {
  id: 'select-funding-source-' + Math.random(),
  label: 'Select Funding Source',
  error: false,
  helperText: '',
  required: false,
  disabled: false,
  fullWidth: true,
  size: 'small',
  variant: 'outlined',
  valueKey: 'payment_value',
};